import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  FlatList,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { Calendar } from "react-native-calendars";
import { CF } from "../theme/turquoiseTheme.js";
import { PageBackground, AppShellBackground, InsightPageBackground } from "./PageBackground.js";
import { GLASS } from "./InspiredBackground.js";
import { todayDateKey, getHabitCount } from "../storage/customHabitsStorage.js";
import { HomeBookTeaser } from "./BookRecommendationsScreen.js";
import { HabitProgressSection } from "./HabitProgressSection.js";
import { ProtectionBar } from "./ProtectionBar.js";
import { HomeDashboard } from "./HomeDashboard.js";
import { getBottomInset, getTopInset } from "../utils/safeInsets.js";
import { isScreenTimeFeaturesEnabled } from "../utils/featureFlags.js";
import { useLanguage } from "../i18n/LanguageContext.js";
import { quoteAuthor, quoteReflection, quoteText } from "../content/dailyQuotes.js";

const SCREEN_TIME_ON = isScreenTimeFeaturesEnabled();

const MAIN_TABS_ALL = [
  { id: "home", labelKey: "tab_home", icon: "🏠" },
  { id: "progress", labelKey: "tab_progress", icon: "📈" },
  { id: "insight", labelKey: "tab_insight", icon: "📊" },
  { id: "library", labelKey: "tab_library", icon: "📚" },
  { id: "community", labelKey: "tab_community", icon: "💬" },
  { id: "screentime", labelKey: "tab_screentime", icon: "⏱" },
];

const MAIN_TABS = SCREEN_TIME_ON ? MAIN_TABS_ALL : MAIN_TABS_ALL.filter((t) => t.id !== "screentime");

const PAGER_SLIDES_ALL = [
  { key: "quote", bg: "quote" },
  { key: "home", bg: "home" },
  { key: "progress", bg: "default" },
  { key: "insight", bg: "default" },
  { key: "library", bg: "default" },
  { key: "community", bg: "default" },
  { key: "screentime", bg: "default" },
];

const PAGER_SLIDES = SCREEN_TIME_ON
  ? PAGER_SLIDES_ALL
  : PAGER_SLIDES_ALL.filter((s) => s.key !== "screentime");
const TOP_BAR_HEIGHT = 52;
const BOTTOM_TAB_HEIGHT = 68;
const TOP_INSET = getTopInset();
const BOTTOM_INSET = getBottomInset();

function getSlides(quoteIntroPending) {
  return quoteIntroPending ? PAGER_SLIDES : PAGER_SLIDES.filter((s) => s.key !== "quote");
}

function getTabOffset(quoteIntroPending) {
  return quoteIntroPending ? 1 : 0;
}

function pagerIndexForTab(activeTabId, quoteIntroPending) {
  const slides = getSlides(quoteIntroPending);
  const idx = slides.findIndex((s) => s.key === activeTabId);
  if (idx >= 0) return idx;
  return Math.max(0, slides.findIndex((s) => s.key === "home"));
}

/**
 * @param {number} deg
 * @param {number} r
 * @param {number} center
 * @param {number} nodeSize
 */
function orbitPosition(deg, r, center, nodeSize) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return {
    left: center + r * Math.cos(rad) - nodeSize / 2,
    top: center + r * Math.sin(rad) - nodeSize / 2,
  };
}

/**
 * @param {{ progress: number, size: number, stroke: number, children?: React.ReactNode }} props
 */
function StreakRing({ progress, size, stroke, children }) {
  const clamped = Math.min(1, Math.max(0, progress));
  const quarter = (n) => (clamped >= n ? CF.ringActive : CF.ringTrack);

  return (
    <View style={{ width: size, height: size, alignItems: "center", justifyContent: "center" }}>
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderColor: CF.ringTrack,
        }}
      />
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderTopColor: quarter(0.25),
          borderRightColor: quarter(0.5),
          borderBottomColor: quarter(0.75),
          borderLeftColor: quarter(1),
        }}
      />
      <View
        style={{
          width: size - stroke * 4,
          height: size - stroke * 4,
          borderRadius: (size - stroke * 4) / 2,
          backgroundColor: CF.bgPanel,
          alignItems: "center",
          justifyContent: "center",
          borderWidth: 1,
          borderColor: CF.border,
        }}
      >
        {children}
      </View>
    </View>
  );
}

/**
 * @param {{
 *   hubSize: number,
 *   orbitR: number,
 *   nodeSize: number,
 *   ringSize: number,
 *   ringStroke: number,
 *   compact: boolean,
 *   currentStreak: number,
 *   goalProgressPct: number,
 *   goalDays: number,
 *   onInsight: () => void,
 *   onProgress: () => void,
 *   onLockbox: () => void,
 *   onParental: () => void,
 *   decorativeOnly?: boolean,
 * }} props
 */
function OrbitalHubStage({
  hubSize,
  orbitR,
  nodeSize,
  ringSize,
  ringStroke,
  compact,
  currentStreak,
  goalProgressPct,
  goalDays,
  onInsight,
  onProgress,
  onLockbox,
  onParental,
  decorativeOnly = false,
}) {
  const { t } = useLanguage();
  const center = hubSize / 2;
  const spin = useRef(new Animated.Value(0)).current;
  const spinReverse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const forward = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 52000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    const reverse = Animated.loop(
      Animated.timing(spinReverse, {
        toValue: 1,
        duration: 76000,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    );
    forward.start();
    reverse.start();
    return () => {
      forward.stop();
      reverse.stop();
    };
  }, [spin, spinReverse]);

  const rotateCW = spin.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });
  const rotateCCW = spinReverse.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "-360deg"] });

  const orbitNodes = decorativeOnly
    ? []
    : [
        { key: "insight", deg: 315, label: t("tab_insight"), icon: "📈", onPress: onInsight, variant: "primary" },
        { key: "progress", deg: 45, label: t("tab_progress"), icon: "◎", onPress: onProgress, variant: "cyan" },
        ...(SCREEN_TIME_ON
          ? [
              { key: "parental", deg: 135, label: "Parental", icon: "🛡️", onPress: onParental, variant: "ghost" },
              { key: "lockbox", deg: 225, label: "Lockbox", icon: "🔒", onPress: onLockbox, variant: "ghost" },
            ]
          : []),
      ];

  const ringScales = [0.38, 0.58, 0.78, 0.98];

  return (
    <View style={[orbitalStyles.stage, { width: hubSize, height: hubSize }]}>
      <Animated.View
        pointerEvents="none"
        style={[orbitalStyles.ringLayer, { transform: [{ rotate: rotateCW }] }]}
      >
        {ringScales.map((scale) => (
          <View
            key={`cw-${scale}`}
            style={[
              orbitalStyles.ringGuide,
              {
                width: hubSize * scale,
                height: hubSize * scale,
                borderRadius: (hubSize * scale) / 2,
                left: (hubSize - hubSize * scale) / 2,
                top: (hubSize - hubSize * scale) / 2,
              },
            ]}
          />
        ))}
      </Animated.View>

      <Animated.View
        pointerEvents="none"
        style={[orbitalStyles.ringLayer, { transform: [{ rotate: rotateCCW }] }]}
      >
        {[0.48, 0.68, 0.88].map((scale) => (
          <View
            key={`ccw-${scale}`}
            style={[
              orbitalStyles.ringGuideInner,
              {
                width: hubSize * scale,
                height: hubSize * scale,
                borderRadius: (hubSize * scale) / 2,
                left: (hubSize - hubSize * scale) / 2,
                top: (hubSize - hubSize * scale) / 2,
              },
            ]}
          />
        ))}
      </Animated.View>

      <Animated.View
        pointerEvents="none"
        style={[orbitalStyles.ringLayer, { transform: [{ rotate: rotateCW }] }]}
      >
        {orbitNodes.map((node) => (
          <View
            key={`spoke-${node.key}`}
            style={[
              orbitalStyles.spokeWrap,
              orbitPosition(node.deg, orbitR, center, nodeSize),
            ]}
          >
            <View
              style={[
                orbitalStyles.connector,
                {
                  width: orbitR - nodeSize * 0.35,
                  transform: [{ rotate: `${node.deg}deg` }],
                },
              ]}
            />
          </View>
        ))}
      </Animated.View>

      {!decorativeOnly
        ? orbitNodes.map((node) => {
        const pos = orbitPosition(node.deg, orbitR, center, nodeSize);
        const bg =
          node.variant === "cyan" ? CF.cyan : node.variant === "primary" ? CF.primaryDark : CF.bgCard;
        const glow = node.variant === "cyan" ? CF.cyanGlow : CF.tealGlow;

        return (
          <View key={node.key} style={[orbitalStyles.nodeWrap, pos, { width: nodeSize, height: nodeSize }]}>
            <Pressable
              onPress={node.onPress}
              style={({ pressed }) => [
                orbitalStyles.orbitNode,
                {
                  width: nodeSize,
                  height: nodeSize,
                  borderRadius: nodeSize / 2,
                  backgroundColor: bg,
                  opacity: pressed ? 0.88 : 1,
                  shadowColor: glow,
                },
              ]}
            >
              {node.icon ? <Text style={orbitalStyles.orbitIcon}>{node.icon}</Text> : null}
              <Text style={[orbitalStyles.orbitLabel, compact && orbitalStyles.orbitLabelCompact]} numberOfLines={2}>
                {node.label}
              </Text>
            </Pressable>
          </View>
        );
      })
        : null}

      <View
        style={[
          orbitalStyles.centerWrap,
          {
            width: ringSize,
            height: ringSize,
            left: center - ringSize / 2,
            top: center - ringSize / 2,
          },
        ]}
      >
        <StreakRing progress={goalProgressPct / 100} size={ringSize} stroke={ringStroke}>
          <Text style={orbitalStyles.streakLabel}>{t("insight_active_streak").toUpperCase()}</Text>
          <Text style={[orbitalStyles.streakValue, compact && orbitalStyles.streakValueCompact]}>{currentStreak}</Text>
          <Text style={orbitalStyles.streakUnit}>{t("home_days").toLowerCase()}</Text>
          <Text style={orbitalStyles.streakGoal}>
            {goalProgressPct}% · {t("streak_goal_label")} {goalDays}d
          </Text>
        </StreakRing>
      </View>
    </View>
  );
}

const orbitalStyles = StyleSheet.create({
  stage: { alignSelf: "center", position: "relative" },
  ringLayer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
  },
  ringGuide: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "rgba(45, 212, 191, 0.14)",
  },
  ringGuideInner: {
    position: "absolute",
    borderWidth: 1,
    borderColor: "rgba(34, 211, 238, 0.1)",
    borderStyle: "dashed",
  },
  spokeWrap: { position: "absolute", zIndex: 1 },
  connector: {
    position: "absolute",
    height: 1,
    backgroundColor: "rgba(45, 212, 191, 0.28)",
    left: "50%",
    top: "50%",
  },
  nodeWrap: { position: "absolute", zIndex: 3 },
  orbitNode: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: CF.borderStrong,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.85,
    shadowRadius: 10,
    elevation: 6,
    paddingHorizontal: 4,
  },
  orbitIcon: { fontSize: 14, marginBottom: 1 },
  orbitLabel: { fontSize: 9, fontWeight: "800", color: CF.text, textAlign: "center", lineHeight: 11 },
  orbitLabelCompact: { fontSize: 8, lineHeight: 10 },
  centerWrap: { position: "absolute", zIndex: 4, alignItems: "center", justifyContent: "center" },
  streakLabel: { fontSize: 9, fontWeight: "800", color: CF.textMuted, letterSpacing: 1.5 },
  streakValue: { fontSize: 30, fontWeight: "900", color: CF.primaryBright, lineHeight: 32, marginTop: -2 },
  streakValueCompact: { fontSize: 26, lineHeight: 28 },
  streakUnit: { fontSize: 11, fontWeight: "700", color: CF.textSoft, marginTop: -2 },
  streakGoal: { fontSize: 9, color: CF.cyan, marginTop: 2, fontWeight: "600", textAlign: "center" },
});

/**
 * @param {{
 *   habitName: string,
 *   startDate?: string,
 *   currentStreak: number,
 *   bestStreak: number,
 *   goalDays: number,
 *   goalProgressPct: number,
 *   totalAttempts: number,
 *   weeklyScore: number,
 *   notificationBadgeCount: number,
 *   quoteOfTheDay: { text: string, author?: string } | null,
 *   quoteIntroPending?: boolean,
 *   markedDates: object,
 *   customHabits: import('../storage/customHabitsStorage.js').CustomHabit[],
 *   habitLogs: import('../storage/customHabitsStorage.js').HabitLogs,
 *   showProtectionBar?: boolean,
 *   onNavigate: (page: string) => void,
 *   onMarkSuccess: () => void,
 *   onMarkFail: () => void,
 *   onUrge: () => void,
 *   onDayPress: (day: { dateString: string }) => void,
 *   onQuoteSeen: () => void | Promise<void>,
 *   onHabitIncrement: (id: string) => void,
 *   onHabitDecrement: (id: string) => void,
 *   onAddHabit: (payload: { name: string, icon: string, dailyGoal: number }) => void,
 *   onRemoveHabit: (id: string) => void,
 *   insightPanel: React.ReactNode,
 *   libraryPanel: React.ReactNode,
 *   communityPanel: React.ReactNode,
 *   screenTimePanel: React.ReactNode,
 *   onMainTabChange?: (tabId: string) => void,
 *   activeTabId?: string,
 *   hubResumeKey?: number,
 * }} props
 */
export function HomeHubScreen({
  habitName,
  startDate,
  currentStreak,
  bestStreak,
  goalDays,
  goalProgressPct,
  totalAttempts,
  weeklyScore,
  notificationBadgeCount,
  quoteOfTheDay,
  quoteIntroPending = false,
  markedDates,
  customHabits,
  habitLogs,
  showProtectionBar = false,
  onNavigate,
  onMarkSuccess,
  onMarkFail,
  onUrge,
  onDayPress,
  onQuoteSeen,
  onHabitIncrement,
  onHabitDecrement,
  onAddHabit,
  onRemoveHabit,
  insightPanel,
  libraryPanel,
  communityPanel,
  screenTimePanel,
  onMainTabChange,
  activeTabId,
  hubResumeKey = 0,
}) {
  const { t, locale, setLocale } = useLanguage();
  const mainTabs = useMemo(
    () => MAIN_TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) })),
    [t],
  );
  const { width: pageWidth, height: windowHeight } = useWindowDimensions();
  const compact = windowHeight < 740;
  const hubSize = compact ? 228 : 252;
  const orbitR = compact ? 88 : 98;
  const nodeSize = compact ? 54 : 58;
  const ringSize = compact ? 108 : 120;
  const ringStroke = compact ? 5 : 6;

  const pagerRef = useRef(null);
  const [layoutHeight, setLayoutHeight] = useState(0);
  const [tabIndex, setTabIndex] = useState(() => pagerIndexForTab(activeTabId, quoteIntroPending));
  const [menuOpen, setMenuOpen] = useState(false);
  const [statTab, setStatTab] = useState("BEST");
  const tabOffset = getTabOffset(quoteIntroPending);

  const scrollPagerToTab = useCallback(
    (tabId, animated = false) => {
      const pagerIdx = pagerIndexForTab(tabId, quoteIntroPending);
      setTabIndex(pagerIdx);
      requestAnimationFrame(() => {
        pagerRef.current?.scrollToIndex({ index: pagerIdx, animated });
      });
    },
    [quoteIntroPending],
  );

  const bottomHighlight = tabIndex - tabOffset;

  useEffect(() => {
    if (quoteIntroPending) {
      setTabIndex(0);
      pagerRef.current?.scrollToIndex({ index: 0, animated: false });
    }
  }, [quoteIntroPending]);

  const prevQuoteIntroRef = useRef(quoteIntroPending);

  useEffect(() => {
    if (prevQuoteIntroRef.current && !quoteIntroPending) {
      const idx = pagerIndexForTab(activeTabId || "home", false);
      setTabIndex(idx);
      requestAnimationFrame(() => {
        pagerRef.current?.scrollToIndex({ index: idx, animated: false });
      });
    }
    prevQuoteIntroRef.current = quoteIntroPending;
  }, [quoteIntroPending, activeTabId]);

  useEffect(() => {
    if (!activeTabId) return;
    scrollPagerToTab(activeTabId, false);
  }, [activeTabId, scrollPagerToTab]);

  /** Returning from Screen Time sub-screens — activeTabId may not change, so hubResumeKey forces scroll */
  useEffect(() => {
    if (!hubResumeKey || !activeTabId) return;
    scrollPagerToTab(activeTabId, false);
  }, [hubResumeKey, activeTabId, scrollPagerToTab]);

  const scrollToPager = useCallback(
    (pagerIndex) => {
      const slides = getSlides(quoteIntroPending);
      const safe = Math.max(0, Math.min(pagerIndex, slides.length - 1));
      setTabIndex(safe);
      const tabId = slides[safe]?.key ?? "home";
      if (safe >= tabOffset) onMainTabChange?.(tabId);
      pagerRef.current?.scrollToIndex({ index: safe, animated: true });
    },
    [onMainTabChange, quoteIntroPending, tabOffset],
  );

  const scrollToTab = useCallback(
    (bottomIndex) => scrollToPager(bottomIndex + tabOffset),
    [scrollToPager, tabOffset],
  );

  const dismissQuoteAndContinue = async () => {
    await onQuoteSeen?.();
    scrollToPager(tabOffset);
  };

  const statValue =
    statTab === "BEST"
      ? `${bestStreak} ${t("home_days").toLowerCase()}`
      : statTab === "ATTEMPTS"
        ? `${totalAttempts}`
        : `${weeklyScore}/7`;

  const statLabel =
    statTab === "BEST"
      ? t("home_best_streak")
      : statTab === "ATTEMPTS"
        ? t("home_total_attempts")
        : t("home_success_week");

  const menuItems = useMemo(
    () => [
      ...mainTabs.map((tab, i) => ({
        key: `tab-${tab.id}`,
        type: "item",
        label: tab.label,
        icon: tab.icon,
        badge: 0,
        action: () => scrollToTab(i),
      })),
      { key: "divider", type: "divider" },
      {
        key: "habit-settings",
        type: "item",
        label: t("habit_settings_title"),
        icon: "⚙️",
        badge: 0,
        action: () => onNavigate("habitsettings"),
      },
      { key: "help", type: "item", label: t("menu_help"), icon: "❓", badge: 0, action: () => onNavigate("help") },
      {
        key: "notifications",
        type: "item",
        label: t("menu_notifications"),
        icon: "🔔",
        badge: notificationBadgeCount,
        action: () => onNavigate("notifications"),
      },
    ],
    [mainTabs, notificationBadgeCount, onNavigate, scrollToTab, t],
  );

  const renderQuotePage = () => (
    <PageBackground>
      <View style={styles.quotePageInner}>
        <View style={styles.quoteBrandIcon}>
          <Text style={styles.quoteBrandEmoji}>🔥</Text>
        </View>
        <Text style={styles.quoteBrandName}>Procsystim</Text>
        <Text style={styles.quotePageLabel}>{t("home_daily_encouragement")}</Text>
        {quoteOfTheDay ? (
          <>
            <View style={styles.quoteCardLight}>
              <Text style={styles.quoteTextLight}>"{quoteText(quoteOfTheDay, locale)}"</Text>
              {quoteAuthor(quoteOfTheDay) ? (
                <Text style={styles.quoteAuthorLight}>— {quoteAuthor(quoteOfTheDay)}</Text>
              ) : null}
              {quoteReflection(quoteOfTheDay, locale) ? (
                <View style={styles.quoteReflectionLight}>
                  <Text style={styles.quoteReflectionLabelLight}>{t("quote_reflection_label")}</Text>
                  <Text style={styles.quoteReflectionTextLight}>
                    {quoteReflection(quoteOfTheDay, locale)}
                  </Text>
                </View>
              ) : null}
            </View>
            <Pressable style={styles.continueBtnLight} onPress={() => void dismissQuoteAndContinue()}>
              <Text style={styles.continueBtnTextLight}>{t("home_continue_tracker")}</Text>
            </Pressable>
          </>
        ) : (
          <Text style={styles.quoteMutedLight}>{t("home_quote_loading")}</Text>
        )}
      </View>
    </PageBackground>
  );

  const renderHomeTab = () => (
    <PageBackground>
      <HomeDashboard
        habitName={habitName || "Procsystim"}
        currentStreak={currentStreak}
        goalProgressPct={goalProgressPct}
        startDate={startDate}
        quoteOfTheDay={quoteOfTheDay}
        notificationBadgeCount={notificationBadgeCount}
        compact={compact}
        showProtectionBar={showProtectionBar ? <ProtectionBar /> : null}
        onOpenMenu={() => setMenuOpen(true)}
        onOpenNotifications={() => onNavigate("notifications")}
        onMarkSuccess={onMarkSuccess}
        onMarkFail={onMarkFail}
        onUrge={onUrge}
      />
    </PageBackground>
  );

  const renderProgressPage = () => {
    const sessionsDone = customHabits.reduce((sum, h) => {
      const count = getHabitCount(habitLogs, h.id, todayDateKey());
      return sum + (count >= h.dailyGoal ? 1 : 0);
    }, 0);
    const sessionGoal = Math.max(1, customHabits.length || 1);

    return (
    <PageBackground>
    <ScrollView
      style={styles.pageTransparent}
      contentContainerStyle={styles.progressContent}
      showsVerticalScrollIndicator
      nestedScrollEnabled
      directionalLockEnabled
      keyboardShouldPersistTaps="handled"
      bounces
      overScrollMode="always"
      scrollEventThrottle={16}
    >
      <View style={styles.progressHero}>
        <View style={styles.progressRingPlate}>
          <StreakRing
            progress={sessionsDone / sessionGoal}
            size={compact ? 156 : 176}
            stroke={compact ? 12 : 14}
          >
            <Text style={styles.progressRingValue}>
              {sessionsDone}/{sessionGoal}
            </Text>
            <Text style={styles.progressRingLabel} numberOfLines={2}>
              daily habits
            </Text>
          </StreakRing>
        </View>
      </View>

      <HabitProgressSection
        habits={customHabits}
        logs={habitLogs}
        onIncrement={onHabitIncrement}
        onDecrement={onHabitDecrement}
        onAddHabit={onAddHabit}
        onRemoveHabit={onRemoveHabit}
        darkMode={false}
      />

      <Text style={styles.progressTitle}>{locale === "fr" ? "Calendrier" : "Calendar"}</Text>
      <View style={styles.calendarWrap}>
        <Calendar
          markedDates={markedDates}
          onDayPress={onDayPress}
          dayNamesShort={
            locale === "fr"
              ? ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"]
              : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
          }
          monthNames={
            locale === "fr"
              ? ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"]
              : ["January","February","March","April","May","June","July","August","September","October","November","December"]
          }
          theme={{
            backgroundColor: "transparent",
            calendarBackground: "transparent",
            textSectionTitleColor: GLASS.textSoft,
            selectedDayBackgroundColor: CF.primaryDark,
            selectedDayTextColor: CF.white,
            todayTextColor: GLASS.textMid,
            dayTextColor: GLASS.textDark,
            textDisabledColor: "#94A3B8",
            monthTextColor: GLASS.textDark,
            arrowColor: GLASS.textMid,
          }}
        />
      </View>

      <View style={styles.tabRow}>
        {(locale === "fr"
          ? [{ id: "BEST", label: "MEILLEUR" }, { id: "ATTEMPTS", label: "TENTATIVES" }, { id: "WEEK", label: "SEMAINE" }]
          : [{ id: "BEST", label: "BEST" }, { id: "ATTEMPTS", label: "ATTEMPTS" }, { id: "WEEK", label: "WEEK" }]
        ).map((tab) => (
          <Pressable
            key={tab.id}
            style={[styles.tabBtn, statTab === tab.id && styles.tabBtnActive]}
            onPress={() => setStatTab(tab.id)}
          >
            <Text style={[styles.tabBtnText, statTab === tab.id && styles.tabBtnTextActive]}>{tab.label}</Text>
          </Pressable>
        ))}
      </View>

      <View style={styles.statHighlight}>
        <Text style={styles.statHighlightLabel}>{statLabel}</Text>
        <Text style={styles.statHighlightValue}>{statValue}</Text>
      </View>

      <HomeBookTeaser onOpenLibrary={() => scrollToTab(3)} darkMode={false} />
    </ScrollView>
    </PageBackground>
    );
  };

  const wrapPanel = (panel) => (
    <PageBackground>
      <View style={styles.pageTransparent}>{panel}</View>
    </PageBackground>
  );

  const wrapInsightPanel = (panel) => (
    <InsightPageBackground>
      <View style={styles.pageTransparent}>{panel}</View>
    </InsightPageBackground>
  );

  const allPages = useMemo(
    () => [
      { key: "quote", render: renderQuotePage },
      { key: "home", render: renderHomeTab },
      { key: "progress", render: renderProgressPage },
      { key: "insight", render: () => wrapInsightPanel(insightPanel) },
      { key: "library", render: () => wrapPanel(libraryPanel) },
      { key: "community", render: () => wrapPanel(communityPanel) },
      ...(SCREEN_TIME_ON && screenTimePanel
        ? [{ key: "screentime", render: () => wrapPanel(screenTimePanel) }]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- render fns close over latest props
    [insightPanel, libraryPanel, communityPanel, screenTimePanel, quoteOfTheDay],
  );

  const pages = useMemo(
    () => (quoteIntroPending ? allPages : allPages.filter((p) => p.key !== "quote")),
    [allPages, quoteIntroPending],
  );

  const isHomeSlide = pages[tabIndex]?.key === "home";
  const bottomChrome = BOTTOM_TAB_HEIGHT + 6;
  const pagerHeight = Math.max(
    280,
    (layoutHeight || windowHeight) - TOP_INSET - BOTTOM_INSET - (isHomeSlide ? 0 : TOP_BAR_HEIGHT) - bottomChrome,
  );

  return (
    <View
      style={[styles.root, { paddingTop: TOP_INSET, paddingBottom: BOTTOM_INSET }]}
      onLayout={(e) => {
        const h = e.nativeEvent.layout.height;
        if (h > 0) setLayoutHeight(h);
      }}
    >
      <AppShellBackground />

      {!isHomeSlide ? (
      <View style={styles.topBarGlass}>
        <Pressable style={styles.topBarBtn} onPress={() => setMenuOpen(true)} hitSlop={8}>
          <Text style={styles.menuIcon}>☰</Text>
        </Pressable>
        <Text style={styles.topBarBrand} numberOfLines={1}>
          Procsystim
        </Text>
        <Pressable style={styles.topBarBtn} onPress={() => onNavigate("notifications")} hitSlop={8}>
          <Text style={styles.notifIcon}>🔔</Text>
          {notificationBadgeCount > 0 ? (
            <View style={styles.notifBadge}>
              <Text style={styles.notifBadgeText}>
                {notificationBadgeCount > 9 ? "9+" : notificationBadgeCount}
              </Text>
            </View>
          ) : null}
        </Pressable>
      </View>
      ) : null}

      <FlatList
        key={quoteIntroPending ? "pager-with-quote" : "pager-main"}
        ref={pagerRef}
        style={[styles.pager, { height: pagerHeight }]}
        data={pages}
        horizontal
        pagingEnabled
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        keyExtractor={(item) => item.key}
        initialScrollIndex={pagerIndexForTab(activeTabId, quoteIntroPending)}
        getItemLayout={(_, index) => ({ length: pageWidth, offset: pageWidth * index, index })}
        onScroll={(e) => {
          const idx = Math.round(e.nativeEvent.contentOffset.x / pageWidth);
          if (idx !== tabIndex) setTabIndex(idx);
        }}
        scrollEventThrottle={16}
        onMomentumScrollEnd={(e) => {
          const idx = Math.round(e.nativeEvent.contentOffset.x / pageWidth);
          setTabIndex(idx);
          const slides = getSlides(quoteIntroPending);
          if (idx >= tabOffset) onMainTabChange?.(slides[idx]?.key ?? "home");
        }}
        renderItem={({ item }) => (
          <View style={{ width: pageWidth, height: pagerHeight }}>{item.render()}</View>
        )}
      />

      <View style={styles.bottomTabs}>
        {mainTabs.map((tab, i) => {
          const active = bottomHighlight === i;
          return (
            <Pressable
              key={tab.id}
              style={styles.bottomTab}
              onPress={() => scrollToTab(i)}
            >
              <View style={[styles.bottomTabIconWell, active && styles.bottomTabIconWellActive]}>
                <Text style={[styles.bottomTabIcon, active && styles.bottomTabIconActive]}>{tab.icon}</Text>
              </View>
              <Text style={[styles.bottomTabLabel, active && styles.bottomTabLabelActive]} numberOfLines={1}>
                {tab.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <Modal visible={menuOpen} animationType="slide" transparent onRequestClose={() => setMenuOpen(false)}>
        <Pressable style={styles.menuBackdrop} onPress={() => setMenuOpen(false)}>
          <Pressable style={styles.menuSheet} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.menuTitle}>{t("menu_title")}</Text>
            {menuItems.map((item) =>
              item.type === "divider" ? (
                <View key={item.key} style={styles.menuDivider} />
              ) : (
                <Pressable
                  key={item.key}
                  style={styles.menuRow}
                  onPress={() => {
                    setMenuOpen(false);
                    item.action();
                  }}
                >
                  <Text style={styles.menuRowIcon}>{item.icon}</Text>
                  <Text style={styles.menuRowText}>{item.label}</Text>
                  {item.badge > 0 ? (
                    <View style={styles.menuBadge}>
                      <Text style={styles.menuBadgeText}>{item.badge > 9 ? "9+" : item.badge}</Text>
                    </View>
                  ) : null}
                </Pressable>
              ),
            )}
            <View style={styles.menuLangBlock}>
              <Text style={styles.menuLangLabel}>{t("lang_label")}</Text>
              <View style={styles.menuLangRow}>
                <Pressable
                  style={[styles.menuLangChip, locale === "en" && styles.menuLangChipOn]}
                  onPress={() => setLocale("en")}
                >
                  <Text style={[styles.menuLangChipText, locale === "en" && styles.menuLangChipTextOn]}>
                    {t("lang_english")}
                  </Text>
                </Pressable>
                <Pressable
                  style={[styles.menuLangChip, locale === "fr" && styles.menuLangChipOn]}
                  onPress={() => setLocale("fr")}
                >
                  <Text style={[styles.menuLangChipText, locale === "fr" && styles.menuLangChipTextOn]}>
                    {t("lang_french")}
                  </Text>
                </Pressable>
              </View>
            </View>
            <Pressable style={styles.menuClose} onPress={() => setMenuOpen(false)}>
              <Text style={styles.menuCloseText}>{t("menu_close")}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#7EE2E2", overflow: "hidden" },
  pager: { flexGrow: 0 },
  page: { flex: 1, backgroundColor: "transparent" },
  pageTransparent: { flex: 1, backgroundColor: "transparent" },
  quotePageInner: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  quoteBrandIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: "#14B8A6",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  quoteBrandEmoji: { fontSize: 32 },
  quoteBrandName: { fontSize: 28, fontWeight: "800", color: "#0F172A", marginBottom: 8 },
  quoteCardLight: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 24,
    width: "100%",
    marginVertical: 20,
    shadowColor: "#0F766E",
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
  },
  quoteTextLight: { fontSize: 18, lineHeight: 28, color: "#334155", textAlign: "center" },
  quoteAuthorLight: { marginTop: 14, fontSize: 14, color: "#64748B", textAlign: "center" },
  quoteReflectionLight: {
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#CBD5E1",
  },
  quoteReflectionLabelLight: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "#64748B",
    textAlign: "center",
    marginBottom: 6,
  },
  quoteReflectionTextLight: {
    fontSize: 14,
    lineHeight: 20,
    color: "#475569",
    textAlign: "center",
  },
  quoteMutedLight: { color: "#64748B", fontSize: 15 },
  continueBtnLight: {
    backgroundColor: "#14B8A6",
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 999,
    width: "100%",
    alignItems: "center",
  },
  continueBtnTextLight: { color: "#fff", fontWeight: "800", fontSize: 16 },
  quotePageLabel: {
    fontSize: 13,
    fontWeight: "700",
    color: CF.primary,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 16,
  },
  quoteCard: {
    backgroundColor: CF.bgPanel,
    borderRadius: 20,
    padding: 22,
    borderWidth: 1,
    borderColor: CF.border,
    width: "100%",
    marginBottom: 20,
  },
  quoteText: { fontSize: 18, lineHeight: 28, color: CF.text, fontStyle: "italic", textAlign: "center" },
  quoteTextCompact: { fontSize: 16, lineHeight: 24 },
  quoteAuthor: { marginTop: 12, fontSize: 13, color: CF.textMuted, textAlign: "center" },
  quoteMuted: { color: CF.textMuted, fontSize: 15 },
  continueBtn: {
    backgroundColor: CF.primaryDark,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: CF.borderStrong,
  },
  continueBtnText: { color: CF.bgDeep, fontWeight: "800", fontSize: 15 },
  hubPage: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 6,
    justifyContent: "space-between",
  },
  hubPageCompact: {
    paddingTop: 6,
    paddingBottom: 4,
  },
  protectionWrap: { marginBottom: 4 },
  hubTop: { gap: 8 },
  topBarGlass: {
    height: TOP_BAR_HEIGHT,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    backgroundColor: GLASS.tabBar,
    borderBottomWidth: 1,
    borderBottomColor: GLASS.tabBarBorder,
  },
  topBarBtn: { position: "relative", padding: 6, minWidth: 40, alignItems: "center" },
  topBarBrand: {
    flex: 1,
    textAlign: "center",
    fontSize: 15,
    fontWeight: "900",
    letterSpacing: 2.2,
    color: GLASS.textDark,
  },
  menuIcon: { fontSize: 22, color: GLASS.textMid, fontWeight: "700" },
  notifIcon: { fontSize: 20, opacity: 0.7 },
  notifBadge: {
    position: "absolute",
    top: 0,
    right: 0,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: CF.fail,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 3,
  },
  notifBadgeText: { color: CF.white, fontSize: 9, fontWeight: "800" },
  bottomTabs: {
    height: BOTTOM_TAB_HEIGHT + 6,
    flexDirection: "row",
    paddingTop: 8,
    paddingBottom: 4,
    backgroundColor: GLASS.tabBar,
    borderTopWidth: 1,
    borderTopColor: GLASS.tabBarBorder,
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
  },
  bottomTab: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
  },
  bottomTabIconWell: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: GLASS.iconWell,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 3,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.5)",
  },
  bottomTabIconWellActive: {
    backgroundColor: GLASS.iconWellActive,
    borderColor: CF.primary,
    borderWidth: 1.5,
  },
  bottomTabIcon: { fontSize: 18, opacity: 0.72 },
  bottomTabIconActive: { opacity: 1, transform: [{ scale: 1.06 }] },
  bottomTabLabel: { fontSize: 9, fontWeight: "700", color: GLASS.label, marginTop: 1 },
  bottomTabLabelActive: { color: GLASS.textDark, fontWeight: "800" },
  todayQuotePill: {
    backgroundColor: CF.bgPanel,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: CF.border,
  },
  todayQuoteLabel: { fontSize: 10, fontWeight: "800", color: CF.primary, letterSpacing: 0.8, marginBottom: 2 },
  todayQuoteSnippet: { fontSize: 12, color: CF.textSoft, lineHeight: 16 },
  menuBtn: {
    alignSelf: "center",
    paddingVertical: 6,
    paddingHorizontal: 24,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: CF.border,
    backgroundColor: CF.bgCard,
  },
  menuBtnText: { color: CF.primaryBright, fontWeight: "700", fontSize: 13 },
  hubBottom: { gap: 8 },
  screenTimeBtn: {
    alignSelf: "center",
    paddingVertical: 10,
    paddingHorizontal: 28,
    borderRadius: 999,
    backgroundColor: CF.bgPanel,
    borderWidth: 1,
    borderColor: CF.borderStrong,
  },
  screenTimeBtnText: { color: CF.primaryBright, fontWeight: "800", fontSize: 14 },
  actionRow: { flexDirection: "row", gap: 10 },
  actionBtn: { flex: 1, paddingVertical: 12, borderRadius: 12, alignItems: "center" },
  successBtn: { backgroundColor: CF.primaryDark },
  failBtn: { backgroundColor: "#BE123C" },
  actionBtnText: { color: CF.white, fontWeight: "800", fontSize: 15 },
  urgeBtn: {
    alignSelf: "center",
    paddingVertical: 10,
    paddingHorizontal: 40,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: CF.purpleAccent,
    backgroundColor: "rgba(167, 139, 250, 0.12)",
  },
  urgeBtnText: { color: CF.purpleAccent, fontWeight: "800", fontSize: 14 },
  progressContent: { paddingHorizontal: 16, paddingTop: 12, paddingBottom: 56, flexGrow: 1 },
  progressTitle: { fontSize: 20, fontWeight: "800", color: GLASS.textDark, marginBottom: 10 },
  progressHero: { alignItems: "center", marginBottom: 16, marginTop: 8 },
  progressRingPlate: {
    padding: 20,
    borderRadius: 999,
    backgroundColor: GLASS.cardStrong,
    borderWidth: 2,
    borderColor: GLASS.border,
    shadowColor: CF.primaryDark,
    shadowOpacity: 0.15,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  progressRingValue: { fontSize: 40, fontWeight: "900", color: GLASS.textDark, letterSpacing: -1 },
  progressRingLabel: {
    fontSize: 10,
    fontWeight: "700",
    color: GLASS.label,
    marginTop: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    textAlign: "center",
    paddingHorizontal: 10,
    maxWidth: 96,
  },
  calendarWrap: {
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: GLASS.border,
    marginBottom: 16,
    backgroundColor: GLASS.cardStrong,
  },
  tabRow: { flexDirection: "row", gap: 8, marginBottom: 12 },
  tabBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: GLASS.card,
    borderWidth: 1,
    borderColor: GLASS.border,
    alignItems: "center",
  },
  tabBtnActive: { backgroundColor: CF.primaryDark, borderColor: CF.primaryDark },
  tabBtnText: { fontSize: 11, fontWeight: "800", color: GLASS.textSoft, letterSpacing: 0.5 },
  tabBtnTextActive: { color: CF.white },
  statHighlight: {
    backgroundColor: GLASS.cardStrong,
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: GLASS.border,
    marginBottom: 18,
  },
  statHighlightLabel: { fontSize: 12, color: GLASS.textSoft, fontWeight: "600", marginBottom: 4 },
  statHighlightValue: { fontSize: 24, fontWeight: "900", color: GLASS.textDark },
  menuBackdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.55)", justifyContent: "flex-end" },
  menuSheet: {
    backgroundColor: CF.bgPanel,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: 32,
    borderTopWidth: 1,
    borderColor: CF.border,
  },
  menuTitle: { fontSize: 20, fontWeight: "800", color: CF.primaryBright, marginBottom: 12 },
  menuRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: CF.border,
    gap: 12,
  },
  menuRowIcon: { fontSize: 20, width: 28, textAlign: "center" },
  menuRowText: { fontSize: 16, fontWeight: "600", color: CF.textSoft, flex: 1 },
  menuDivider: {
    height: 1,
    backgroundColor: CF.border,
    marginVertical: 8,
  },
  menuBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: CF.fail,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 6,
  },
  menuBadgeText: { color: CF.white, fontSize: 11, fontWeight: "800" },
  menuClose: { marginTop: 16, alignItems: "center", paddingVertical: 12 },
  menuCloseText: { color: CF.textMuted, fontWeight: "700" },
  menuLangBlock: { marginTop: 18, marginBottom: 4 },
  menuLangLabel: { fontSize: 12, fontWeight: "800", color: "#6B7280", marginBottom: 8, letterSpacing: 0.4 },
  menuLangRow: { flexDirection: "row", gap: 8 },
  menuLangChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  menuLangChipOn: { borderColor: "#0D9488", backgroundColor: "#CCFBF1" },
  menuLangChipText: { fontWeight: "700", color: "#6B7280", fontSize: 13 },
  menuLangChipTextOn: { color: "#0F766E" },
});
